// src/features/owner/sections/users/UsersSection.tsx — full Users list with real suspend/ban actions.
import { useState } from 'react';
import { useOwnerUsers } from '@/hooks';
import { useOwnerAction } from '@/hooks/useOwnerAction';
import { useSession } from '@/hooks/useSession';
import { Avatar } from '@/ui/Avatar';
import { Icon } from '@/icons';
import { Button } from '@/ui';
import { OwnerPanel, DataTable, ConfirmActionModal } from '../../components';
import type { Column } from '../../components';
import type { OwnerUserRow, ModerationCategory } from '@/core/models';

export function UsersSection() {
  const { data, loading, error, refetch } = useOwnerUsers();
  const action = useOwnerAction();
  const { isSuperuser } = useSession();
  const [pending, setPending] = useState<{ row: OwnerUserRow; kind: 'suspend' | 'ban' } | null>(null);

  const columns: Column<OwnerUserRow>[] = [
    {
      key: 'user',
      header: 'User',
      sortBy: (r) => r.handle,
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar name={r.name} avatarKey={r.avatarKey} size={26} />
          <span>
            <b>{r.name}</b> {r.verified && <Icon name="verify" size={12} />}
            <br />
            <span style={{ opacity: 0.6 }}>{r.handle}</span>
          </span>
        </span>
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      sortBy: (r) => r.joinedAt,
      render: (r) => new Date(r.joinedAt).toLocaleDateString(),
    },
    {
      key: 'posts',
      header: 'Posts',
      align: 'right',
      sortBy: (r) => r.postsCount,
      render: (r) => String(r.postsCount),
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (r) => (r.premium ? 'Premium' : 'Free'),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setPending({ row: r, kind: 'suspend' })}>
            Suspend
          </Button>
          {isSuperuser && (
            <Button variant="ghost" onClick={() => setPending({ row: r, kind: 'ban' })}>
              Ban
            </Button>
          )}
        </span>
      ),
    },
  ];

  if (loading) return <p style={{ opacity: 0.6 }}>Loading…</p>;

  return (
    <OwnerPanel title={`Users${data ? ` (${data.length})` : ''}`}>
      <DataTable<OwnerUserRow>
        columns={columns}
        rows={data ?? []}
        rowKey={(r) => r.id}
        emptyIcon="users"
        emptyMessage={error ?? 'No users yet'}
      />
      <ConfirmActionModal
        open={!!pending}
        title={pending?.kind === 'ban' ? 'Ban user' : 'Suspend user'}
        body={
          pending
            ? `${pending.kind === 'ban' ? 'Ban' : 'Suspend'} ${pending.row.handle}? This is a last-resort operator action tied to the five moderation categories and is logged to Audit Logs.`
            : ''
        }
        confirmLabel={pending?.kind === 'ban' ? 'Ban' : 'Suspend'}
        actionKind={pending?.kind ?? undefined}
        onConfirm={async (category, note) => {
          if (!pending) return;
          const cat = (category ?? 'coordinated-abuse') as ModerationCategory;
          if (pending.kind === 'ban') {
            await action.ban(pending.row.id, cat, note);
          } else {
            await action.suspend(pending.row.id, cat, note);
          }
          refetch();
        }}
        onClose={() => setPending(null)}
      />
    </OwnerPanel>
  );
}
